import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import { Row, Col } from 'reactstrap';

import { GridWithCustomFields } from '@skunexus/custom-fields';
import { Button, Modal } from '@skunexus/component-catalog';

import styles from './styles.module.css';

function ResolvePurchaseOrder({
    details,
    values,
    isLoading,
    totalSize,
    refreshToken,
    gridSelections,
    fetchRows,
    cancelPO,
    cancelItems,
}) {
    const [modalData, setModalData] = useState({
        isOpen: false,
        isLoading: false,
        isError: false,
    });
    const gridName = useMemo(() => `purchaseOrderLineItem-resolve-grid-${Math.random()}`, [modalData.isOpen]);
    const selectedItems = get(gridSelections, gridName, []);

    const handleOpenModal = () => {
        setModalData((state) => ({
            ...state,
            isOpen: true,
        }));
    };

    const handleCloseModal = () => {
        setModalData((state) => ({
            ...state,
            isOpen: false,
        }));
    };

    const handleCancel = () => {
        setModalData((state) => ({
            ...state,
            isLoading: true,
        }));
        cancelPO({
            id: details.id,
            data: {
                item_ids: selectedItems,
            },
            onSuccess: () => {
                setModalData((state) => ({
                    ...state,
                    isLoading: false,
                    isOpen: false,
                }));

                window.location.reload();
            },
            onFailure: () => {
                setModalData((state) => ({
                    ...state,
                    isLoading: false,
                }));
            },
        });
    };

    const handleCancelAndResend = () => {
        if (selectedItems.length === 0) {
            setModalData((state) => ({
                ...state,
                isError: true,
            }));
        } else {
            setModalData((state) => ({
                ...state,
                isLoading: true,
                isError: false,
            }));
            cancelItems({
                id: details.id,
                data: {
                    item_ids: selectedItems,
                },
                onSuccess: () => {
                    setModalData((state) => ({
                        ...state,
                        isLoading: false,
                        isOpen: false,
                    }));
                    window.location.reload();
                },
                onFailure: () => {
                    setModalData((state) => ({
                        ...state,
                        isLoading: false,
                    }));
                },
            });
        }
    };

    const getData = (config) => {
        fetchRows({ id: details.id, config });
    };

    /* eslint-disable react/prop-types, react/no-array-index-key */
    const gridProps = {
        values,
        customFields: {
            startPosition: 10,
            hiddenFields: ['product_description'],
        },
        columns: [
            {
                field: 'customValues.product_name',
                label: 'Name',
                type: 'string',
                isSortable: true,
                isFilterable: true,
            },
            {
                field: 'customValues.product_sku',
                label: 'SKU',
                type: 'string',
                isSortable: true,
                isFilterable: true,
            },
            {
                field: 'relatedVendorProduct.sku',
                gqlFields: ['product_id', 'relatedVendorProduct.sku'],
                label: 'Vendor SKU',
                type: 'string',
                isSortable: true,
                isFilterable: true,
            },
            {
                field: 'quantity',
                label: 'Qty',
                type: 'number',
                isSortable: true,
                isFilterable: true,
            },
            {
                field: 'trackingCodes.tracking_code',
                label: 'Tracking Number',
                type: 'string',
                isSortable: true,
                isFilterable: true,
                // eslint-disable-next-line react/no-unstable-nested-components
                template: ({ originalValue: { row: { trackingCodes = [] } } = {} }) => (
                    <div>
                        {trackingCodes.map((trackingCode, index) => (
                            <div key={index}>{get(trackingCode, 'tracking_code', '-')}</div>
                        ))}
                    </div>
                ),
            },
            {
                field: 'trackingCodes.quantity',
                label: 'Qty Included In Shipment',
                type: 'number',
                isSortable: true,
                isFilterable: true,
                // eslint-disable-next-line react/no-unstable-nested-components
                template: ({ originalValue: { row: { trackingCodes = [] } } = {} }) => (
                    <div>
                        {trackingCodes.map((trackingCode, index) => (
                            <div key={index}>{get(trackingCode, 'quantity', '-')}</div>
                        ))}
                    </div>
                ),
            },
            {
                field: 'price',
                label: 'Unit',
                type: 'number',
                isSortable: true,
                isFilterable: true,
            },
            {
                field: 'total_price',
                label: 'Amount',
                type: 'number',
                isSortable: true,
                isFilterable: true,
            },
        ],
        itemsCount: totalSize,
        config: {
            isLoading,
            bulkDelete: { isVisible: false },
            withPagination: true,
            isSelectable: true,
        },
    };
    /* eslint-enable react/prop-types, react/no-array-index-key */

    return (
        <Row className="mt-2">
            <Col>
                <hr />
                <Button color="primary" size="sm" className="mt-2" onClick={handleOpenModal}>
                    Resolve Purchase Order
                </Button>
                <Modal
                    headerLabel="Resolve Purchase Order"
                    isOpen={modalData.isOpen}
                    size="lg"
                    className={styles.fullWidth}
                    resetOnClick={handleCancel}
                    resetLabel={`Cancel PO and Redecide Order (${selectedItems.length})`}
                    confirmOnClick={handleCancelAndResend}
                    confirmLabel={`Cancel Items and Resend PO (${selectedItems.length})`}
                    cancelOnClick={handleCloseModal}
                    cancelLabel="Close"
                    footerProps={{
                        confirmProps: {
                            disabled: selectedItems.length === 0 || modalData.isLoading,
                        },
                        cancelProps: {
                            disabled: modalData.isLoading,
                        },
                        resetProps: {
                            disabled: modalData.isLoading,
                        },
                    }}
                >
                    <p>
                        <strong>Please select the item(s) that is unavailable</strong>
                    </p>
                    <GridWithCustomFields
                        domain="purchaseOrderLineItem"
                        id={gridName}
                        data={gridProps}
                        getData={getData}
                        refreshToken={refreshToken}
                    />
                    {modalData.isError && <p className={styles.error}>To cancel items, select at least on of them.</p>}
                </Modal>
            </Col>
        </Row>
    );
}

ResolvePurchaseOrder.propTypes = {
    details: PropTypes.object,
    values: PropTypes.array,
    isLoading: PropTypes.bool.isRequired,
    totalSize: PropTypes.number.isRequired,
    refreshToken: PropTypes.number.isRequired,
    gridSelections: PropTypes.object,
    fetchRows: PropTypes.func.isRequired,
    cancelPO: PropTypes.func.isRequired,
    cancelItems: PropTypes.func.isRequired,
};

ResolvePurchaseOrder.defaultProps = {
    details: {},
    values: [],
    gridSelections: {},
};

export default ResolvePurchaseOrder;
